/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable, makeStateKey, TransferState } from '@angular/core';

import { v4 as uuidV4 } from 'uuid';

import {
  catchError,
  from,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  timeout,
} from 'rxjs';

import { CookieService } from 'ngx-cookie-service';

import { Experiment, ExperimentClient } from '@amplitude/experiment-js-client';

import { AnalyticsBrowser } from '@segment/analytics-next';
import { ConfigurationService, IAmplitudeAppConfig } from '@sondermind/configuration';
import {
  EvaluationContext,
  FeatureFlagKey,
  FeatureFlagVariant,
  IEvaluationOptions,
  IFeatureFlagService
} from '@sondermind/data-access/feature-flags';
import { Logger } from '@sondermind/datadog-logging';
import { SentryClient } from '@sondermind/sentry-client';
import { isBotUserAgent } from '@sondermind/utilities/tools';

// Amplitude FF service setup according to SDK guide:
// https://www.docs.developers.amplitude.com/experiment/sdks/javascript-sdk/#configuration

// Default timeout for fetch requests
const DEFAULT_TIMEOUT = 3000;

export const ANON_ID_KEY = 'ajs_anonymous_id';

@Injectable({
  providedIn: 'root'
})
export class AmplitudeFeatureFlagService implements IFeatureFlagService {
  private experimentClient?: ExperimentClient;
  private analytics?: AnalyticsBrowser;
  private initialized = false;
  private deviceId: string;
  private anonymousId: string;
  private userId: string | null = null;

  constructor(
    private configurationService: ConfigurationService<IAmplitudeAppConfig>,
    private transferState: TransferState,
    private logger: Logger,
    private cookieService: CookieService,
  ) { }

  initialize$(): Observable<boolean> {
    if (this.initialized) {
      return of(true);
    }

    if (isBotUserAgent()) {
      this.logger.info('Skipping Amplitude initialization for bot traffic.');
      return of(false);
    }

    const config = this.configurationService.env.app;
    if (!config?.amplitudeDeploymentKey) {
      this.logger.warn('Skipping Amplitude Initialization because of missing deployment key.');
      return of(false);
    }

    // uncomment debug property for amplitude console logging
    this.experimentClient = Experiment.initialize(config.amplitudeDeploymentKey, {
      // debug: this.configurationService.env.sentryDebug,
      serverUrl: 'https://amplitude.sondermind.com',
      flagsServerUrl: 'https://flag.sondermind.com',
      automaticExposureTracking: false,
      exposureTrackingProvider: {
        track: (exposure) => {
          this.trackExposureEvent(exposure);
        }
      }
    });

    return this.anonymousId$().pipe(
      switchMap((anonymousId) => {
        // Storing the anonymousId as it might be needed later (by consumers this service)
        this.anonymousId = anonymousId;
        this.deviceId = anonymousId;
        this.initialized = true;
        return of(true);
      }),
      catchError(
        (error) => {
          SentryClient.captureException(error);
          return of(false);
        }
      )
    );
  }

  /**
   * Sets the Amplitude "user_id" used for feature flag evaluation.
   */
  setUserId(userId: string | null): void {
    this.userId = userId;
  }

  evaluate$<TPayload = unknown>(
    flagKey: FeatureFlagKey,
    defaultValue: TPayload,
    options?: IEvaluationOptions
  ): Observable<FeatureFlagVariant<TPayload> | null> {
    // Check if the feature flag is already cached in the transfer state
    const key = makeStateKey<FeatureFlagVariant<TPayload>>(flagKey);
    if (this.transferState.hasKey(key)) {
      return of(this.transferState.get(
        key,
        { key: 'off', payload: defaultValue } as FeatureFlagVariant<TPayload>)
      );
    }

    return this.initialize$().pipe(
      switchMap((initialized) => {
        if (!initialized || !this.experimentClient) {
          return of(null);
        }

        const amplitudeUser: { device_id: string; user_id?: string; } = { device_id: this.deviceId };

        if (this.userId) {
          amplitudeUser.user_id = this.userId;
        }

        return from(this.experimentClient.fetch(amplitudeUser)).pipe(
          switchMap(() => this.evaluateVariant$<TPayload>(flagKey, defaultValue)),
          timeout(options?.timeout ?? DEFAULT_TIMEOUT)
        );
      }),
      catchError((error: unknown) => {
        SentryClient.captureException(error);

        this.logger.error(
          `Error evaluating feature flag ${flagKey}. Returning default value or null.`,
          { error, deviceId: this.deviceId }
        );
        return of(this.getErrorVariant(defaultValue));
      }),
      shareReplay(1)
    );
  }

  /**
   * Returns an observable that emits the boolean value of the feature flag.
   *
   * @param flagKey - The key of the feature flag to evaluate.
   * @param defaultValue - The default value to return if the feature flag is not found or cannot be evaluated.
   * @returns An observable that emits the boolean value of the feature flag.
   *
   * Note: `false` is the implicit default value
   */
  isEnabled$(flagKey: FeatureFlagKey): Observable<boolean> {
    return this.evaluate$<boolean>(flagKey, false).pipe(
      map((variant) => ['on', 'true', 'enabled'].includes(variant?.key ?? ''))
    );
  }

  private evaluateVariant$<TPayload = unknown>(
    flagKey: string,
    defaultValue: TPayload
  ): Observable<FeatureFlagVariant<TPayload> | null> {
    if (!this.experimentClient) {
      this.logger.warn(`Experiment client not initialized. Returning null.`, { flagKey });
      return of(null);
    }

    try {
      const variant = this.experimentClient.variant(flagKey);

      if (!this.isValidVariant(variant)) {
        this.logger.warn(
          `Variant not found for flag ${flagKey}. Returning default value or null.`,
          { flagKey, deviceId: this.deviceId }
        );
        return of(this.getErrorVariant(defaultValue));
      }

      // If experiment is off, return default value or null
      if (this.isExperimentOff(variant)) {
        return of(defaultValue ? this.createDefaultOffVariant(defaultValue) : null);
      }

      // Don't track exposure here - let the consumer call trackExposure$ when actually exposed
      return of({
        key: variant.key,
        payload: variant.payload as TPayload
      });
    } catch (error: unknown) {
      SentryClient.captureException(error);
      this.logger.error(
        `Error evaluating variant for flag ${flagKey}. Returning default value or null.`,
        { error, deviceId: this.deviceId }
      );
      return of(this.getErrorVariant(defaultValue));
    }
  }

  getVariant$<TPayload = unknown>(
    context: EvaluationContext,
    flagKey: string,
    defaultValue: TPayload = null as TPayload
  ): Observable<FeatureFlagVariant<TPayload> | null> {
    return this.getVariantsForUser$(context.accountId).pipe(
      map((variants) => {
        const variant = variants[flagKey];

        if (!this.isValidVariant(variant)) {
          return null;
        }

        if (this.isExperimentOff(variant)) {
          return defaultValue ? this.createDefaultOffVariant(defaultValue) : null;
        }

        // Only track exposure for valid, active experiments
        if (this.experimentClient) {
          this.experimentClient.exposure(flagKey);
        }

        return {
          key: variant.key,
          payload: variant.payload as TPayload
        };
      }),
      catchError((error: unknown) => {
        SentryClient.captureException(error);
        this.logger.error(
          `Error getting variant for flag ${flagKey}. Returning default value or null.`,
          { error, deviceId: this.deviceId }
        );

        return of(this.getErrorVariant(defaultValue));
      })
    );
  }

  /**
   * Creates a variant to use in the case of an error. Use default value if provided.
   * If default value is boolean, create an appropriate 'on' or 'off' variant. Otherwise fallback to null.
   */
  private getErrorVariant<TPayload = unknown>(
    defaultValue: TPayload
  ): FeatureFlagVariant<TPayload> | null {
    // No input provided (explicitly handle null/undefined)
    if (defaultValue === undefined || defaultValue === null) {
      return null;
    }

    // Boolean input provided
    if (typeof defaultValue === 'boolean') {
      return defaultValue
        ? this.createDefaultOnVariant(defaultValue)
        : this.createDefaultOffVariant(defaultValue);
    }

    // Non-boolean input provided (including falsy values like '' or 0)
    return { key: 'default', payload: defaultValue };
  }

  private getVariantsForUser$(userId: string): Observable<Record<string, FeatureFlagVariant>> {
    return this.initialize$().pipe(
      switchMap((initialized) => {
        if (!initialized || !this.experimentClient) {
          return of({});
        }

        return from(this.experimentClient.fetch({ user_id: userId })).pipe(
          map((response: any) => {
            // Unwrap variants from nested Amplitude SDK response structure (response.variants.cache)
            const variants = response?.variants?.cache || response?.variants || response;

            return Object.entries(variants).reduce((acc, [key, variant]: [string, FeatureFlagVariant]) => {
              // Skip variants with undefined keys
              if (!variant?.key) {
                return acc;
              }

              acc[key] = {
                key: variant.key,
                payload: variant.payload
              };
              return acc;
            }, {} as Record<string, FeatureFlagVariant>);
          }),
          catchError(() => of({}))
        );
      })
    );
  }

  anonymousId$(): Observable<string> {
    // If the anonymousId is already set, return it
    if (this.anonymousId) {
      return of(this.anonymousId);
    }

    const key = makeStateKey<string>(ANON_ID_KEY);
    const serverAnonId = this.transferState.get(key, null);

    const segmentWriteKey = this.configurationService.env.app?.segmentWriteKey;
    if (segmentWriteKey && !isBotUserAgent()) {
      // TODO: MER-3311 - Remove duplicate load call in favor of using SegmentService directly
      const enablePixels = !!this.configurationService.env.app?.enableSegmentPixelDest;
      const integrations = {
        All: true,
        'Facebook Pixel': enablePixels,
        'TikTok Pixel': enablePixels,
        'Google AdWords New': enablePixels,
        'Bing Ads': enablePixels,
        'Segment.io': {
          apiHost: 'segment-api.sondermind.com/v1'
        }
      };

      this.analytics = AnalyticsBrowser.load(
        {
          writeKey: segmentWriteKey,
          cdnURL: 'https://segment.sondermind.com'
        },
        { integrations }
      );
    } else {
      const sentryMessageAry = ['No segment write key was provided for the amplitude feature flag service.'];

      let anonymousId = this.readAnonId();
      if (anonymousId) {
        sentryMessageAry.push('Using existing id read from LocalStorage.');
      } else if (serverAnonId) {
        sentryMessageAry.push('Using existing id read from cookies.');
        anonymousId = serverAnonId;
        // save to local storage
        this.saveAnonId(serverAnonId);
      } else {
        sentryMessageAry.push('Continuing by using a generated device ID.');

        anonymousId = this.generateAnonId();
        // save to local storage
        this.saveAnonId(anonymousId);
        // set cookie for server
        this.cookieService.set(ANON_ID_KEY, anonymousId);
      }

      SentryClient.captureMessage(sentryMessageAry.join(' '));

      return of(anonymousId);
    }

    return from(this.analytics.user()).pipe(
      switchMap((analyticsUser) => {
        let anonymousId = analyticsUser?.anonymousId();
        if (serverAnonId && serverAnonId !== anonymousId) {
          this.logger.warn(
            'Found anon ID from server, and it is different from browser. Overwriting browser value',
            { serverAnonId, anonymousId }
          );
          // override the value
          analyticsUser?.anonymousId(serverAnonId);
          anonymousId = serverAnonId;
        }
        return of(anonymousId);
      }),
      catchError((error) => {
        SentryClient.captureException(error);
        let anonymousId = serverAnonId || this.readAnonId();
        if (!anonymousId) {
          anonymousId = this.generateAnonId();
          // save to local storage
          this.saveAnonId(anonymousId);
          // store in cookie to be availble on server
          this.cookieService.set(ANON_ID_KEY, anonymousId);
        }

        return of(anonymousId);
      })
    );
  }

  private generateAnonId(): string {
    return uuidV4();
  }

  private readAnonId(): string | null {
    return localStorage.getItem(ANON_ID_KEY)?.replaceAll('"', '');
  }

  private saveAnonId(value: string): void {
    localStorage.setItem(ANON_ID_KEY, value);
  }

  /** Creates a default variant with 'off' key and the provided payload */
  private createDefaultOffVariant<TPayload>(
    payload: TPayload
  ): FeatureFlagVariant<TPayload> {
    return { key: 'off', payload };
  }

  /** Creates a default variant with 'on' key and the provided payload */
  private createDefaultOnVariant<TPayload>(
    payload: TPayload
  ): FeatureFlagVariant<TPayload> {
    return { key: 'on', payload };
  }

  /**
   * Checks if a variant is valid (not null/undefined and has a key)
   */
  private isValidVariant(variant: any): boolean {
    return !!variant?.key;
  }

  /**
   * Checks if the experiment is considered "off" based on the variant key
   */
  private isExperimentOff(variant: any): boolean {
    const offKeys = ['off', 'disabled', 'false'];
    return offKeys.includes(variant?.key?.toLowerCase());
  }

  trackExposure$(flagKey: FeatureFlagKey, variant: FeatureFlagVariant): Observable<void> {
    return this.initialize$().pipe(
      switchMap((initialized) => this.handleExposureTracking(initialized, flagKey, variant)),
      catchError((error: unknown) => this.handleExposureError(error, flagKey, variant))
    );
  }

  private handleExposureTracking(
    initialized: boolean,
    flagKey: FeatureFlagKey,
    variant: FeatureFlagVariant
  ): Observable<void> {
    if (!this.canTrackExposure(initialized)) {
      this.logger.warn(`Cannot track exposure - service not initialized`, { flagKey });
      return of(undefined);
    }

    return this.trackAmplitudeExposure(flagKey, variant);
  }

  private canTrackExposure(initialized: boolean): boolean {
    return initialized && !!this.experimentClient;
  }

  private trackAmplitudeExposure(flagKey: FeatureFlagKey, variant: FeatureFlagVariant): Observable<void> {
    try {
      this.experimentClient.exposure(flagKey);
      this.logger.debug(`Feature flag exposure tracked successfully`, {
        flagKey,
        variant: variant.key
      });
      return of(undefined);
    } catch (error: unknown) {
      return this.handleExposureError(error, flagKey, variant);
    }
  }

  private handleExposureError(
    error: unknown,
    flagKey: FeatureFlagKey,
    variant: FeatureFlagVariant
  ): Observable<void> {
    const errorContext = { error, flagKey, variant: variant.key };
    SentryClient.captureException(error);
    this.logger.error(`Failed to track feature flag exposure`, errorContext);
    return of(undefined);
  }

  private trackExposureEvent(exposure: { flag_key: string; variant?: string; experiment_key?: string; }): void {
    try {
      this.analytics?.track('$exposure', exposure);

      this.logger.debug('Exposure event tracked successfully via analytics', { exposure });
    } catch (error) {
      this.logger.error('Error tracking exposure event via analytics', { error, exposure });
      SentryClient.captureException(error);
    }
  }
}
